/**
 * Profesor Page (TeacherPanel)
 * 
 * Full-page teacher panel for managing courses and meeting links.
 * Integrates TeacherLogin, TeacherCourseList, MeetingLinkEditor, and StudentProgressTable.
 * Handles authentication state and session management.
 * 
 * Requirements: 7.1, 8.1
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LogOut, 
  GraduationCap, 
  ArrowLeft,
  Video,
  Users,
  RefreshCw
} from 'lucide-react';

// Components
import TeacherLogin, { 
  isTeacherAuthenticated, 
  getTeacherToken, 
  getTeacherData, 
  clearTeacherAuth 
} from '../components/TeacherLogin';
import TeacherCourseList from '../components/TeacherCourseList';
import MeetingLinkEditor from '../components/MeetingLinkEditor';
import StudentProgressTable from '../components/StudentProgressTable';
import CourseBadge from '../components/CourseBadge';

/**
 * Payment Service base URL from environment variable
 */
const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:3001';

/**
 * Profesor Page Component
 */
export default function Profesor() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [teacher, setTeacher] = useState(null);
  const [token, setToken] = useState(null);

  // Courses state
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState(null);

  // Selected course state
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Students state
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);

  // CSV export state
  const [exportLoading, setExportLoading] = useState(false);

  /**
   * Check authentication on mount
   */
  useEffect(() => {
    if (isTeacherAuthenticated()) {
      const storedToken = getTeacherToken();
      const storedTeacher = getTeacherData();
      setIsAuthenticated(true);
      setToken(storedToken);
      setTeacher(storedTeacher);
    }
  }, []);

  /**
   * Fetch courses when authenticated
   */
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchCourses();
    }
  }, [isAuthenticated, token]);

  /**
   * Fetch teacher's courses from API
   */
  const fetchCourses = useCallback(async () => {
    if (!token) return;

    setCoursesLoading(true);
    setCoursesError(null);

    try {
      const response = await fetch(`${PAYMENT_API_URL}/api/teacher/courses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          handleLogout();
          return;
        }
        throw new Error(data.error || 'Error al cargar cursos');
      }

      setCourses(data.courses || []);
    } catch (err) {
      setCoursesError(err.message || 'Error al cargar cursos');
    } finally {
      setCoursesLoading(false);
    }
  }, [token]);

  /**
   * Fetch students for selected course
   */
  const fetchStudents = useCallback(async (courseId) => {
    if (!token || !courseId) return;

    setStudentsLoading(true);
    setStudentsError(null);

    try {
      const response = await fetch(
        `${PAYMENT_API_URL}/api/teacher/courses/${courseId}/students`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        throw new Error(data.error || 'Error al cargar estudiantes');
      }

      setStudents(data.students || []);
    } catch (err) {
      setStudentsError(err.message || 'Error al cargar estudiantes');
    } finally {
      setStudentsLoading(false);
    }
  }, [token]);

  /**
   * Handle successful login
   */
  const handleLoginSuccess = (teacherData, authToken) => {
    setIsAuthenticated(true);
    setTeacher(teacherData);
    setToken(authToken);
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    clearTeacherAuth();
    setIsAuthenticated(false);
    setTeacher(null);
    setToken(null);
    setCourses([]);
    setSelectedCourse(null);
    setStudents([]);
  };

  /**
   * Handle course selection
   */
  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setStudents([]);
    fetchStudents(course.id);
  };

  /**
   * Handle back to course list
   */
  const handleBackToCourses = () => {
    setSelectedCourse(null);
    setStudents([]);
  };

  /**
   * Handle meeting link update
   */
  const handleMeetingLinkUpdated = (updateData) => {
    // Update the selected course with new meeting link data
    if (selectedCourse) {
      setSelectedCourse({
        ...selectedCourse,
        meetingLink: updateData.meetingLink,
        meetingLinkUpdatedAt: updateData.updatedAt,
      });

      // Also update in courses list
      setCourses(prevCourses =>
        prevCourses.map(course =>
          course.id === selectedCourse.id
            ? {
                ...course,
                meetingLink: updateData.meetingLink,
                meetingLinkUpdatedAt: updateData.updatedAt,
              }
            : course
        )
      );
    }
  };

  /**
   * Handle CSV export
   */
  const handleExportCSV = async () => {
    if (!token || !selectedCourse) return;

    setExportLoading(true);

    try {
      const response = await fetch(
        `${PAYMENT_API_URL}/api/teacher/courses/${selectedCourse.id}/students/export`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        const data = await response.json();
        throw new Error(data.error || 'Error al exportar CSV');
      }

      // Get the CSV content
      const csvContent = await response.text();

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `progreso-${selectedCourse.name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      // Could show a toast notification here
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Background decorations */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo and title */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white">
                    Panel de Profesor
                  </h1>
                  <p className="text-xs text-white/50">
                    Instituto Valparaíso
                  </p>
                </div>
              </div>

              {/* User info and logout */}
              {isAuthenticated && teacher && (
                <div className="flex items-center gap-4">
                  <span className="text-sm text-white/70 hidden sm:block">
                    {teacher.name || teacher.email}
                  </span>
                  <motion.button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm
                               text-white/70 hover:text-white
                               bg-white/5 hover:bg-white/10 rounded-lg
                               border border-white/10 hover:border-white/20
                               transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">Cerrar Sesión</span>
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AnimatePresence mode="wait">
            {/* Login Screen */}
            {!isAuthenticated && (
              <motion.div
                key="login"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex items-center justify-center min-h-[calc(100vh-12rem)]"
              >
                <TeacherLogin onLoginSuccess={handleLoginSuccess} />
              </motion.div>
            )}

            {/* Authenticated - Course List View */}
            {isAuthenticated && !selectedCourse && (
              <motion.div
                key="courses"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <TeacherCourseList
                  courses={courses}
                  loading={coursesLoading}
                  error={coursesError}
                  onCourseSelect={handleCourseSelect}
                  onRefresh={fetchCourses}
                />
              </motion.div>
            )}

            {/* Authenticated - Course Detail View */}
            {isAuthenticated && selectedCourse && (
              <motion.div
                key="course-detail"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-8"
              >
                {/* Back button and course header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <motion.button
                      onClick={handleBackToCourses}
                      className="flex items-center gap-2 px-3 py-2 text-sm
                                 text-white/70 hover:text-white
                                 bg-white/5 hover:bg-white/10 rounded-lg
                                 border border-white/10 hover:border-white/20
                                 transition-all"
                      whileHover={{ scale: 1.02, x: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Volver
                    </motion.button>
                    <div>
                      <div className="flex items-center gap-3">
                        <h2 className="text-2xl font-bold text-white">
                          {selectedCourse.name}
                        </h2>
                        <CourseBadge type={selectedCourse.type} />
                      </div>
                      <p className="text-sm text-white/50 mt-1">
                        {selectedCourse.enrolledCount || 0} estudiantes inscritos
                      </p>
                    </div>
                  </div>

                  <motion.button
                    onClick={() => {
                      fetchCourses();
                      fetchStudents(selectedCourse.id);
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm
                               text-white/70 hover:text-white
                               bg-white/5 hover:bg-white/10 rounded-lg
                               border border-white/10 hover:border-white/20
                               transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Actualizar
                  </motion.button>
                </div>

                {/* Two column layout for sync courses, single column for async */}
                <div className={`grid gap-8 ${
                  selectedCourse.type === 'synchronous' 
                    ? 'lg:grid-cols-3' 
                    : 'lg:grid-cols-1'
                }`}>
                  {/* Meeting Link Editor - Only for synchronous courses */}
                  {selectedCourse.type === 'synchronous' && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="lg:col-span-1"
                    >
                      <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                        <div className="flex items-center gap-2 mb-4">
                          <Video className="w-5 h-5 text-cyan-400" />
                          <h3 className="text-lg font-semibold text-white">
                            Enlace de Reunión
                          </h3>
                        </div>
                        <MeetingLinkEditor
                          courseId={selectedCourse.id}
                          courseName={selectedCourse.name}
                          currentLink={selectedCourse.meetingLink || ''}
                          lastUpdatedAt={selectedCourse.meetingLinkUpdatedAt}
                          token={token}
                          onLinkUpdated={handleMeetingLinkUpdated}
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* Student Progress Table */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className={selectedCourse.type === 'synchronous' ? 'lg:col-span-2' : ''}
                  >
                    <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                      <StudentProgressTable
                        students={students}
                        loading={studentsLoading}
                        error={studentsError}
                        onExportCSV={handleExportCSV}
                        exportLoading={exportLoading}
                        courseName={selectedCourse.name}
                        onRefresh={() => fetchStudents(selectedCourse.id)}
                      />
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/10 mt-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <p className="text-center text-sm text-white/40">
              © {new Date().getFullYear()} Instituto Valparaíso. Todos los derechos reservados.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
